import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useTranslations } from 'hooks/useTranslations';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { Chips, EChipsVariants } from 'components/atoms/Chips';

interface IAccessoriesCard {
  data: ITableDataSource;
  handleEdit?: (item: ITableDataSource) => void;
  handleView?: (args: string) => void;
  renderColor: (data: ITableDataSource) => {
    border: string;
    stateField: EChipsVariants;
    background: string;
  };
}

export enum EAccessoriesTableLabels {
  serialNumber = 'accessoriesTable.accordion.serialNumber.column',
  exhibit = 'accessoriesTable.accordion.exhibit.column',
  fireCycleId = 'accessoriesTable.accordion.firecycleId.column',
  accessoryType = 'accessoriesTable.accordion.accessoryType.column',
  hostType = 'accessoriesTable.accordion.hostType.column',
  owner = 'accessoriesTable.accordion.owner.column',
  keeper = 'accessoriesTable.accordion.keeper.column',
  accessoryState = 'accessoriesTable.accordion.accessoryState.column',
  exhibitNumber = 'accessoriesTable.accordion.exhibitNumber.column',
  manufacturer = 'accessoriesTable.accordion.manufacturer.column',
  productName = 'accessoriesTable.accordion.productName.column',
}

const AccessoriesCard = ({ data, handleEdit, handleView, renderColor }: IAccessoriesCard) => {
  const { t } = useTranslations();
  const { hasPermissions } = usePermission();
  return (
    <Grid
      container
      sx={{
        border: `1px solid ${renderColor(data).border}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: renderColor(data).background,
      }}
    >
      <Grid item xs={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAccessoriesTableLabels.fireCycleId)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.fireCycleId)}</Typography>
      </Grid>
      <Grid item xs={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAccessoriesTableLabels.serialNumber)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.serialNumber)}</Typography>
      </Grid>
      <Grid item xs={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAccessoriesTableLabels.exhibitNumber)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.exhibitNumber)}</Typography>
      </Grid>
      <Grid item xs={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAccessoriesTableLabels.accessoryType)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.ancillarySpecificType)}</Typography>
      </Grid>
      <Grid item xs={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAccessoriesTableLabels.hostType)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.accessoryHostType)}</Typography>
      </Grid>
      <Grid item xs={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAccessoriesTableLabels.manufacturer)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.manufacturer)}</Typography>
      </Grid>
      <Grid item xs={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAccessoriesTableLabels.productName)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.accessoryProductName)}</Typography>
      </Grid>
      <Grid item xs={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAccessoriesTableLabels.owner)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.ownerName)}</Typography>
      </Grid>
      <Grid item xs={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAccessoriesTableLabels.keeper)}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.keeperName)}</Typography>
      </Grid>
      <Grid item xs={6} p={2}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(EAccessoriesTableLabels.accessoryState)}
        </Typography>
        {data.state ? <Chips text={data.state} variant={renderColor(data).stateField} /> : '-'}
      </Grid>
      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${colors.primary25}`,
            lg: 'none',
          },
        }}
        p={2}
      >
        <Grid item xs={12} sm={3} sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('general.actions.button')}:{' '}
          </Typography>
        </Grid>

        <Grid container item xs={12} sm={9} spacing={1} justifyContent="flex-end">
          {handleEdit && hasPermissions([EPermission.EDIT_ANCILLARY]) && (
            <Grid item xs={12} sm={6}>
              <Button
                label={t('accesoriesTable.accordion.edit.button')}
                variant={EButtonVariants.outlined}
                onClick={() => handleEdit(data)}
                size="small"
                fullWidth
              />
            </Grid>
          )}
          {handleView && (
            <Grid item xs={12} sm={6}>
              <Button
                label={t('accesoriesTable.accordion.details.button')}
                variant={EButtonVariants.outlined}
                onClick={() => handleView(data.id as string)}
                size="small"
                fullWidth
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export { AccessoriesCard };
